import './LoginRobo.scss';
// @ vendors
import React, { useEffect, useReducer, useRef } from 'react';
import { formatText } from 'core/i18n';
import PropTypes from 'prop-types';
// @ utilities
import classNames from 'classnames';
import UtagHelper from 'utilities/utagHelper';
// @ helpers
import { isCountrySpain } from 'utilities/multiLanguageHelper';
import { buildLoginPayload } from 'utilities/RASuitabilityTestHelper';
// @ constants
import { PUBLICA_URL } from 'constants/index';
import { PATH_ROBO_TI, PATH_GBI_TI } from 'constants/uTagConstants/paths';
import {
    EVENT_ACTION_LOGIN_ROBO,
    EVENT_DETAIL_LOGIN_ROBO,
    EVENT_CATEGORY_SUITABILITY_TEST,
    EVENT_ACTION_PASSWORD_RECOVERY,
    EVENT_DETAIL_PASSWORD_RECOVERY,
    EVENT_DETAIL_LOGIN_ROBO_SUCCESS,
} from 'constants/uTagConstants/RAConstants';
import { ROBO } from 'constants/userProfile';
import { SUITABILITY_TEST_VALID } from 'constants/RAConstants/index';
// @ components
import { OtpBlock } from 'components/commons/OtpBlock/OtpBlock.jsx';

const initialState = {
    passOne: '',
    passTwo: '',
    passThree: '',
    passFour: '',
    isValidPass: false,
    disabled: false,
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'RESET':
            return initialState;
        case 'IS_VALID_PASS':
            return { ...state, isValidPass: true };
        case 'RESET_INPUT_VALUE':
            return { ...state, [action.name]: initialState[action.name] };
        case 'CHANGE_INPUT_VALUE':
            return { ...state, [action.name]: action.value };
        case 'DISABLE_INPUTS':
            return { ...state, disabled: true };
        default:
            return state;
    }
};

export const LoginRobo = ({
    resetErrors,
    login,
    resetOTP,
    verifyLoginOtpRobo,
    showPreLoader,
    hidePreLoader,
    loginRobo,
    className,
    userProfile: { showOTP, password },
    isValidPass,
    userProfile,
    startTest,
    immSuitabilityTest,
    immCheckStatus,
}) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const passOneRef = useRef();
    const passTwoRef = useRef();
    const passThreeRef = useRef();
    const passFourRef = useRef();

    const loginRoboError = loginRobo.get('error');
    const loginRoboErrorCount = loginRobo.get('errorCount');
    const iconClass = classNames({ 'icons--icon-check': isValidPass || showOTP }, { hidden: !isValidPass && !showOTP });

    const doLoginRobo = password => {
        const payload = { ...buildLoginPayload(userProfile), password };
        resetOTP();
        showPreLoader();
        login(userProfile.accessToken, payload, valid => {
            if (valid) {
                const isRobo = userProfile.investmentType === ROBO;
                const pathForTag = isRobo ? PATH_ROBO_TI : PATH_GBI_TI;
                dispatch({ type: 'IS_VALID_PASS' });
                UtagHelper.recordEventRoboGBI(
                    pathForTag,
                    EVENT_CATEGORY_SUITABILITY_TEST,
                    EVENT_ACTION_LOGIN_ROBO,
                    EVENT_DETAIL_LOGIN_ROBO_SUCCESS,
                    userProfile
                );
                startTest?.();
            } else {
                dispatch({ type: 'RESET' });
                passOneRef.current.focus();
            }
            hidePreLoader();
        });
    };

    useEffect(() => {
        resetErrors();
        resetOTP();
    }, []);

    useEffect(() => {
        if (loginRoboErrorCount > 2) {
            dispatch({ type: 'DISABLE_INPUTS' });
        }
    }, [loginRoboErrorCount, dispatch]);

    useEffect(() => {
        const password = state.passOne + state.passTwo + state.passThree + state.passFour;

        if (password.length === 4) {
            doLoginRobo(password);
        }
    }, [state.passOne, state.passTwo, state.passThree, state.passFour]);

    const onHandleValidateOTP = otpCode => {
        const accessToken = userProfile.accessToken;
        const dataToken = userProfile.dataToken;
        const otpCodeU = otpCode ? otpCode.toUpperCase() : '';
        const payload = { otp: otpCodeU, dataToken };
        verifyLoginOtpRobo(payload, accessToken, valid => {
            if (valid) {
                startTest?.();
            }
        });
    };

    const onHandleReloadOtp = () => {
        doLoginRobo(password);
    };

    const handleCursor = event => {
        const iLength = 1;
        const { value } = event.target;
        const name = event.target.id;

        if (value.length >= iLength) {
            dispatch({ type: 'CHANGE_INPUT_VALUE', name, value });

            switch (name) {
                case 'passOne':
                    passTwoRef.current.focus();
                    break;
                case 'passTwo':
                    passThreeRef.current.focus();
                    break;
                case 'passThree':
                    passFourRef.current.focus();
                    break;
                case 'passFour':
                    break;
                default:
                    break;
            }
        }
    };

    const handleOnKeyDown = e => {
        if (e.keyCode === 8) {
            e.preventDefault();
            const name = e.target.id;

            dispatch({ type: 'RESET_INPUT_VALUE', name });

            switch (name) {
                case 'passOne':
                    break;
                case 'passTwo':
                    passOneRef.current.focus();
                    break;
                case 'passThree':
                    passTwoRef.current.focus();
                    break;
                case 'passFour':
                    passThreeRef.current.focus();
                    break;
                default:
                    break;
            }
        }
    };

    const handleOnClick = () => {
        const isRobo = userProfile.investmentType === ROBO;
        const pathForTag = isRobo ? PATH_ROBO_TI : PATH_GBI_TI;

        UtagHelper.recordEventRoboGBI(
            pathForTag,
            EVENT_CATEGORY_SUITABILITY_TEST,
            EVENT_ACTION_LOGIN_ROBO,
            EVENT_DETAIL_LOGIN_ROBO,
            userProfile
        );
    };

    const handleOnClickPasswordRecover = () => {
        const isRobo = userProfile.investmentType === ROBO;
        const pathForTag = isRobo ? PATH_ROBO_TI : PATH_GBI_TI;

        UtagHelper.recordEventRoboGBI(
            pathForTag,
            EVENT_CATEGORY_SUITABILITY_TEST,
            EVENT_ACTION_PASSWORD_RECOVERY,
            EVENT_DETAIL_PASSWORD_RECOVERY,
            userProfile
        );
    };

    const buildOtpView = showOTP => {
        const isValidOTP = immSuitabilityTest.get('isValidOTP');
        const loadingOTP = immSuitabilityTest.get('loadingReloadOTPCode') || immSuitabilityTest.get('isSigningTest');
        const wasOTPValidated = immSuitabilityTest.get('wasOTPValidated');
        const otpsuccess =
            (immSuitabilityTest.get('isValidOTP') || immCheckStatus.get('checkStatus') === SUITABILITY_TEST_VALID) &&
            !immSuitabilityTest.get('isSigningTest') &&
            !immSuitabilityTest.get('signTestFailed');

        return showOTP ? (
            <OtpBlock
                autoValidate
                otpModel={'userProfile.otpCode'}
                className="login-robo-otp"
                validateOTP={onHandleValidateOTP}
                reloadOtp={onHandleReloadOtp}
                isValidOTP={isValidOTP}
                loadingOTP={loadingOTP}
                wasOTPValidated={wasOTPValidated}
                otpSuccess={otpsuccess}
            />
        ) : null;
    };

    const errorMessage = () => {
        if (loginRoboError) {
            if (loginRoboErrorCount > 2) {
                const forgotPassLink = isCountrySpain() ? 'he-olvidado-mi-clave' : 'i-forgot-my-password';

                return (
                    <label className="error-label">
                        {formatText('loginRobo-labelErrorRecovery1')}{' '}
                        <a
                            onClick={handleOnClickPasswordRecover}
                            href={PUBLICA_URL + forgotPassLink}
                            target="_blank"
                            className="recovery-link"
                            rel="noreferrer"
                        >
                            {formatText('loginRobo-laberErrorRecovery2')}
                        </a>
                    </label>
                );
            }

            return <label className="error-label">{formatText('loginRobo-labelError')}</label>;
        }

        return null;
    };

    return (
        <div className={className}>
            <div>
                <label className="title">{formatText('loginRobo-title')}</label>
            </div>
            <div>
                <label className="key-label">{formatText('loginRobo-keyLabel')}</label>
            </div>
            <div className="login-robo__wrapper">
                <div className={`${className}__input-group`}>
                    <input
                        type="password"
                        onChange={handleCursor}
                        onKeyDown={handleOnKeyDown}
                        onClick={handleOnClick}
                        id="passOne"
                        aria-label="passOne"
                        maxLength="1"
                        className="input-robo"
                        disabled={state.disabled}
                        value={state.passOne}
                        ref={passOneRef}
                    />
                    <input
                        type="password"
                        onChange={handleCursor}
                        onKeyDown={handleOnKeyDown}
                        id="passTwo"
                        aria-label="passTwo"
                        maxLength="1"
                        className="input-robo"
                        disabled={state.disabled}
                        value={state.passTwo}
                        ref={passTwoRef}
                    />
                    <input
                        type="password"
                        onChange={handleCursor}
                        onKeyDown={handleOnKeyDown}
                        id="passThree"
                        aria-label="passThree"
                        maxLength="1"
                        className="input-robo"
                        disabled={state.disabled}
                        value={state.passThree}
                        ref={passThreeRef}
                    />
                    <input
                        type="password"
                        onChange={handleCursor}
                        onKeyDown={handleOnKeyDown}
                        id="passFour"
                        aria-label="passFour"
                        maxLength="1"
                        className="input-robo"
                        disabled={state.disabled}
                        value={state.passFour}
                        ref={passFourRef}
                    />
                </div>
                <div className={`${className}__icon-wrapper`}>
                    <span className={iconClass}></span>
                </div>
            </div>
            <div>{errorMessage()}</div>
            {buildOtpView(showOTP)}
        </div>
    );
};

LoginRobo.propTypes = {
    resetErrors: PropTypes.func,
    login: PropTypes.func,
    resetOTP: PropTypes.func,
    verifyLoginOtpRobo: PropTypes.func,
    showPreLoader: PropTypes.func,
    hidePreLoader: PropTypes.func,
    startTest: PropTypes.func,
    loginRobo: PropTypes.object,
    className: PropTypes.string,
    showOTP: PropTypes.func,
    password: PropTypes.string,
    isValidPass: PropTypes.bool,
    userProfile: PropTypes.object,
    immSuitabilityTest: PropTypes.object,
    immCheckStatus: PropTypes.object,
};
